// Renders plain text with any URLs or email addresses turned into clickable
// links. Used wherever user-entered free text is displayed (descriptions,
// messages, reviews, replies) so a pasted link becomes a hyperlink.
//
// It also breaks the text into PARAGRAPHS. The container keeps
// whiteSpace:'pre-wrap', which preserves a newline but renders it as a tight
// single break — fine for an address, wrong for a note made of several
// distinct statements. A Good-to-know field reading "Address in Hebrew: …
// Prices: … Friday and motzei Shabbat: … Source: …" arrives as one wall of
// text, and the reader has to find the sentence that concerns them.
//
// A blank line starts a new paragraph with space around it; a single newline
// stays a line break within one. That is what someone typing into the edit
// form expects, and it leaves text with no newlines at all rendering exactly
// as before.

import React from 'react';

// Matches: http(s):// URLs, www. URLs, and bare emails. The URL body stops
// before trailing punctuation so a link at the end of a sentence doesn't eat
// the period/paren/comma.
const PATTERN = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,!?;:)\]}'"]|[^\s<>()[\]]+@[A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,})/gi;

const LINK_STYLE = { color: '#1a4d8f', textDecoration: 'underline', wordBreak: 'break-word' };

// One paragraph's worth of text, with its links turned into anchors.
function linkedParts(str, style) {
  return str.split(PATTERN).map((part, i) => {
    // Odd indexes are the captured matches (links); even indexes are text.
    if (i % 2 === 0) return part;
    const isEmail = part.includes('@') && !/^https?:\/\//i.test(part) && !/^www\./i.test(part);
    const href = isEmail
      ? `mailto:${part}`
      : (/^https?:\/\//i.test(part) ? part : `https://${part}`);
    return (
      <a
        key={i}
        href={href}
        target={isEmail ? undefined : '_blank'}
        rel="noopener noreferrer"
        style={{ ...LINK_STYLE, ...style }}
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    );
  });
}

export default function Linkify({ text, style }) {
  if (text == null || text === '') return null;
  const str = String(text);

  // Split on blank lines. One paragraph means no wrapper at all, so nothing
  // changes for the overwhelming majority of fields.
  const paras = str.split(/\n[ \t]*\n+/).map((p) => p.trim()).filter(Boolean);
  if (paras.length < 2) return <>{linkedParts(str, style)}</>;

  return (
    <>
      {paras.map((p, i) => (
        // Spacing between paragraphs only — no margin above the first or
        // below the last, so the block still sits flush in its container.
        <div key={i} style={i ? { marginTop: '0.7em' } : undefined}>
          {linkedParts(p, style)}
        </div>
      ))}
    </>
  );
}
